import React, { FC, useContext, useMemo, useState } from "react";
import { IUsePaginationStore } from "utils/usePaginationStore";
import { LocalizeText } from "components/localizer";
import VKButton from "components/vkButton";
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import { THeadCell } from "utils/types";
import TableComponent, { IRow } from "components/tableComponent";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ModalContext } from "contexts/modals";
import { AddMember } from "components/drawers/members/addMember";
import TopBar from "./topBar";
import SubscriptionsContext from "contexts/subscriptions";
import { parseISO, isToday } from "date-fns";
import { dateIsPastOrToday } from "utils/helpers";
import { IMembership } from "reducers/memberships";
import { TLanguageTag } from "contexts/language";

interface IProps {
    pagination: IUsePaginationStore<"available-subscriptions">;
    membership: IMembership;
    refreshTable: () => Promise<void>;
}

export const AvailableSubscriptionsTable: FC<IProps> = ({ pagination, membership, refreshTable }) => {
    const theme = useTheme();
    const largeScreen = useMediaQuery(theme.breakpoints.up("xl"));
    const navigate = useNavigate();
    const { openDrawer } = React.useContext(ModalContext);
    const { getBariumRequestMoreMembershipURL } = useContext(SubscriptionsContext);
    const [externalLinkIsLoading, setExternalLinkIsLoading] = useState(false);
    const membershipIsInactive = dateIsPastOrToday(parseISO(membership?.end_date ?? ""));

    const availableSubscriptionTableHeaders: THeadCell[] = [
        {
            id: "id",
            label: <LocalizeText tag="id" />,
        },
        {
            id: "productName",
            label: <LocalizeText tag="productAndCommunity" />,
        },
        { id: "availableFrom", label: <LocalizeText tag="availableFrom" /> },
        { id: "action", label: <></> },

    ];

    const rows = useMemo((): IRow[] => {
        return pagination.results.map((subscription) => ({
            data: [
                subscription.identifier,
                subscription.product_name + " " + subscription.community_title,
                isToday(subscription.earliest_member_activation_date) ? <LocalizeText tag="today" /> : subscription.can_add_associations ? subscription.earliest_member_activation_date : "-",
                <VKButton
                    key={"subscription_" + subscription.id}
                    template="primary"
                    size="small"
                    variant="outlined"
                    sx={{ width: "max-content" }}
                    onClick={() =>
                        openDrawer(
                            AddMember,
                            {
                                subscription,
                                updateSubscription: refreshTable,
                            },
                            "addMember"
                        )
                    }
                    disabled={!subscription.can_add_associations}
                >
                    <LocalizeText tag="addMember" />
                </VKButton>
            ],
        }));
    }, [pagination.results, largeScreen, navigate]);

    const openBariumLink = (): void => {
        getBariumRequestMoreMembershipURL(Number(membership.id)).finally(() => setExternalLinkIsLoading(false));
        setExternalLinkIsLoading(true);
    };

    let customNoDataTag: TLanguageTag = "noAvailableSubscriptionForInactiveMembership";
    if (!membershipIsInactive) {
        customNoDataTag = membership.subscriptions_count > 0 ? "allSubscriptionsAreInUse" : "noAvailableSubscriptionForMembership";
    }

    return (
        <>
            <TopBar
                titleTag="availableSubscriptionTitle"
                singleItem={membershipIsInactive || pagination.isLoading ? null : <VKButton
                    size="small"
                    variant="outlined"
                    template={pagination.totalCount === 0 ? "primary" : undefined}
                    isLoading={externalLinkIsLoading}
                    disabled={externalLinkIsLoading}
                    onClick={() => openBariumLink()} >
                    <LocalizeText tag="requestNewSubscriptions" />
                </VKButton>} />
            <Box>
                <TableComponent
                    headCells={availableSubscriptionTableHeaders}
                    skeletonVariant="options"
                    pagination={pagination}
                    rows={rows}
                    customNoDataTag={customNoDataTag}
                />
            </Box>
        </>
    );
};
